import { createStore } from "zustand/vanilla";
import { UserWorkspace, WorkspaceRoles } from "@/types";
import { switchWorkspaceAction, updateWorkspaceAction } from "@/app/actions/workspace";
import { toast } from "@/components/ui/toast";

export type WorkspaceState = {
  current: UserWorkspace;
  additional: UserWorkspace[];
};

export type WorkspaceActions = {
  updateWorkspace: (workspace: Partial<UserWorkspace>) => void;
  getCurrentWorkspace: () => UserWorkspace;
  switchWorkspace: (newWorkspace: UserWorkspace) => Promise<void>;
};
export type WorkspaceStore = WorkspaceState & WorkspaceActions;

export const defaultInitState: WorkspaceState = {
  current: {
    id: "",
    avatarUrl: "",
    name: "",
    slug: "",
    disabledAt: null,
    companyUrl: null,
    networkSourcedLeadsEnabled: true,
    role: WorkspaceRoles.Member,
    isCurrent: true,
    leftAt: null,
  },
  additional: [],
};

export const createWorkspaceStore = (initState: WorkspaceState = defaultInitState) => {
  return createStore<WorkspaceStore>()((set, get) => ({
    ...initState,

    getCurrentWorkspace: () => get().current,

    updateWorkspace: async workspaceUpdates => {
      const { current, additional } = get();

      // save the previous state in case we need to roll back
      const previousState = { current, additional };

      // optimistic data for the current workspace
      const updatedWorkspace = { ...current, ...workspaceUpdates };

      set({
        current: updatedWorkspace,
        additional,
      });

      try {
        const result = await updateWorkspaceAction({
          id: current.id,
          ...workspaceUpdates,
        });

        if (result.error) {
          throw new Error(result.error);
        }

        // is read in the workspace provider for cross-tab updates
        localStorage.setItem("workspace-update", Date.now().toString());
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : "Unknown error";
        toast.error(`Failed to update workspace: ${errorMessage}`);
        // rollback if things go bad
        set(previousState);
      }
    },
    switchWorkspace: async newWorkspace => {
      const { current, additional } = get();

      // save the previous state in case we need to roll back
      const previousState = { current, additional };

      // optimistically update the current workspace
      set({
        current: newWorkspace,
        additional: [current, ...additional.filter(ws => ws.id !== newWorkspace.id)],
      });

      try {
        await switchWorkspaceAction({ currentId: current.id, newId: newWorkspace.id });
      } catch (e) {
        // roll back if things go bad
        set(previousState);

        throw e;
      }
    },
  }));
};
